import { api } from "./client";

/**
 * Admin-managed Lookups v1.0 — frontend API client for the Roles and
 * Categories taxonomy tables seeded by the AddAdminManagedLookups
 * migration (PR 1) and exposed at <c>/api/admin/roles</c>,
 * <c>/api/admin/categories</c>, <c>/api/roles</c>, and
 * <c>/api/categories</c>.
 *
 * The user-facing GETs (<c>list</c>) drive every read-site that needs
 * to render a role or category label — RoleChip, the Report Template
 * editor's chip group + category dropdown, the Roles &amp; Permissions
 * admin page, etc. The admin endpoints (<c>adminList</c>, <c>create</c>,
 * <c>update</c>, <c>archive</c>, <c>unarchive</c>) drive the
 * <c>/admin/roles</c> and <c>/admin/categories</c> pages.
 */
export interface RoleDto {
  code: string;
  displayName: string;
  description?: string | null;
  isActive: boolean;
  isSystemReserved: boolean;
  displayOrder: number;
  createdAt: string;
  modifiedAt: string;
}

export interface CategoryDto {
  code: string;
  displayName: string;
  description?: string | null;
  isActive: boolean;
  displayOrder: number;
  createdAt: string;
  modifiedAt: string;
}

export interface CreateRolePayload {
  code: string;
  displayName: string;
  description?: string | null;
  displayOrder?: number | null;
}

export interface UpdateRolePayload {
  displayName: string;
  description?: string | null;
  displayOrder?: number | null;
}

export interface CreateCategoryPayload {
  code: string;
  displayName: string;
  description?: string | null;
  displayOrder?: number | null;
}

export interface UpdateCategoryPayload {
  displayName: string;
  description?: string | null;
  displayOrder?: number | null;
}

export const rolesLookupApi = {
  // User-facing — active rows only.
  list: () => api.get<RoleDto[]>("/roles"),

  // Admin CRUD.
  adminList: (includeArchived = false) =>
    api.get<RoleDto[]>("/admin/roles", { includeArchived }),
  adminGet: (code: string) => api.get<RoleDto>(`/admin/roles/${encodeURIComponent(code)}`),
  adminCreate: (body: CreateRolePayload) => api.post<RoleDto>("/admin/roles", body),
  adminUpdate: (code: string, body: UpdateRolePayload) =>
    api.put<RoleDto>(`/admin/roles/${encodeURIComponent(code)}`, body),
  adminArchive: (code: string) =>
    api.post<RoleDto>(`/admin/roles/${encodeURIComponent(code)}/archive`),
  adminUnarchive: (code: string) =>
    api.post<RoleDto>(`/admin/roles/${encodeURIComponent(code)}/unarchive`),
};

export const categoriesApi = {
  // User-facing — active rows only.
  list: () => api.get<CategoryDto[]>("/categories"),

  // Admin CRUD.
  adminList: (includeArchived = false) =>
    api.get<CategoryDto[]>("/admin/categories", { includeArchived }),
  adminGet: (code: string) =>
    api.get<CategoryDto>(`/admin/categories/${encodeURIComponent(code)}`),
  adminCreate: (body: CreateCategoryPayload) => api.post<CategoryDto>("/admin/categories", body),
  adminUpdate: (code: string, body: UpdateCategoryPayload) =>
    api.put<CategoryDto>(`/admin/categories/${encodeURIComponent(code)}`, body),
  adminArchive: (code: string) =>
    api.post<CategoryDto>(`/admin/categories/${encodeURIComponent(code)}/archive`),
  adminUnarchive: (code: string) =>
    api.post<CategoryDto>(`/admin/categories/${encodeURIComponent(code)}/unarchive`),
};
